import { AccessorFnColumnDef, createColumnHelper } from '@tanstack/react-table';
import { FC, useCallback, useMemo, useState } from 'react';
import { Button, Cell, DeleteItemModal, Dropdown, Input, Table } from '../../../../components/common';
import useGetCampaigns from '../../../../service/hooks/useGetCampaigns';
import { ICampaign } from '../../../../types';
import dayjs from 'dayjs';
import { cn } from '../../../../utils/helpers';
import useModal from '../../../../components/partials/Modal/useModal';
import { useDeleteCampaign } from '../../../../service/hooks';
import { Link, useNavigate } from 'react-router-dom';
import { MODAL_TYPES, TYPE_CUSTOM_AUDIENCE } from '../../../../utils/constants';
import { DownloadCampaignModal, StartCampaignModal } from '../components';
import Cookies from 'js-cookie';

const columnHelper = createColumnHelper<ICampaign>();

const SORT_OPTIONS = [
  {
    label: 'City',
    value: 'city',
  },
  {
    label: 'State',
    value: 'state',
  },
];

const ITEMS_PER_PAGE_OPTIONS = [
  {
    label: '10',
    value: 10,
  },
  {
    label: '20',
    value: 20,
  },
  {
    label: '30',
    value: 30,
  },
  {
    label: '40',
    value: 40,
  },
  {
    label: '50',
    value: 50,
  },
];

const Campaign: FC = () => {
  const navigate = useNavigate();
  const { showModal } = useModal();

  const [sort, setSort] = useState('city');
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [search, setSearch] = useState('');

  const { data } = useGetCampaigns({
    page: page - 1,
    size: itemsPerPage,
    sort: `campaignSearchQuery.${sort}`,
    name: searchQuery,
  });
  const { mutate: deleteCampaign, isPending } = useDeleteCampaign();

  const handleSearch = () => {
    setSearchQuery(search);
  };

  const handleDownloadCampaign = (id: string) => {
    window.open(
      `${process.env.REACT_APP_API_END_POINT}api/v1/campaigns/${id}/preview?access_token=${Cookies.get('_token')}`,
      '_blank',
    );
  };

  const handleDownloadProperty = (id: string) => {
    window.open(
      `${process.env.REACT_APP_API_END_POINT}api/v1/campaigns/${id}/preview/re-api-property-details?access_token=${Cookies.get('_token')}`,
      '_blank',
    );
  };

  const handleOpenDeleteModal = useCallback(
    (id: string) => {
      showModal({
        title: 'DELETE CAMPAIGN',
        content: <DeleteItemModal text="campaign" onClickDelete={() => deleteCampaign(id)} isLoading={isPending} />,
        disableOutsideClick: true,
        type: MODAL_TYPES.WARNING,
      });
    },
    [deleteCampaign, isPending, showModal],
  );

  const handleStartCampaign = useCallback(
    (id: string) => {
      showModal({
        title: "RUN A CAMPAIGN",
        content: <StartCampaignModal id={id} />,
        disableOutsideClick: true,
        type: MODAL_TYPES.INFO,
      });
    },
    [showModal],
  );

  const handleOpenDownloadCampaign = useCallback(
    (id: string) => {
      showModal({
        title: 'DOWNLOAD CAMPAIGN',
        content: <DownloadCampaignModal onClickDownload={() => handleDownloadCampaign(id)} />,
        disableOutsideClick: true,
        type: MODAL_TYPES.INFO,
      });
    },
    [showModal],
  );

  const handleOpenDownloadProperty = useCallback(
    (id: string) => {
      showModal({
        title: 'DOWNLOAD PROPERTY',
        content: <DownloadCampaignModal onClickDownload={() => handleDownloadProperty(id)} />,
        disableOutsideClick: true,
        type: MODAL_TYPES.INFO,
      });
    },
    [showModal],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('id', {
        cell: (info) => (
          <Cell data={{ label: info.getValue() }}>
            <div className="flex items-center justify-between p-4">
              <p className="text-sm">{data?.content.find((campaign) => campaign.id === info.getValue())?.name ?? ''}</p>
              <Link to={`/campaign/${info.getValue()}`} className="text-[#1D266F] text-xs">
                View
              </Link>
            </div>
          </Cell>
        ),
        header: () => <span>Campaign</span>,
      }),
      columnHelper.accessor((row) => row.status, {
        id: 'status',
        cell: (info) => <Cell data={{ label: info.getValue() }} />,
        header: () => <span>Status</span>
      }),
      columnHelper.accessor((row) => row.type, {
        id: 'type',
        cell: (info) => <Cell data={{ label: info.getValue() }} />,
        header: () => <span>Type</span>
      }),
      columnHelper.accessor((row) => row.campaignSearchQuery?.stateOrProvince, {
        id: 'state',
        cell: (info) => <Cell data={{ label: info.getValue() }} />,
        header: () => <span>State</span>,
      }),
      columnHelper.accessor((row) => row.campaignSearchQuery?.city, {
        id: 'city',
        cell: (info) => <Cell data={{ label: info.getValue() }} />,
        header: () => <span>City</span>,
      }),
      // columnHelper.accessor((row) => row.repeatDaysOfWeek, {
      //   id: 'repeatDaysOfWeek',
      //   cell: (info) => <Cell data={{ label: info.getValue() }} />,
      //   header: () => <span>Repeat Every</span>,
      // }),
      columnHelper.accessor((row) => row.nextTrigger, {
        id: 'nextTrigger',
        cell: (info) => (
          <Cell data={{ label: info.getValue() ? dayjs(info.getValue()).format('DD MMM YYYY [at] hh:mm a') : null }} />
        ),
        header: () => <span>Next Trigger</span>,
      }),
      columnHelper.accessor((row) => row.id, {
        id: 'actions',
        cell: (info) => (
          <Cell data={{ label: info.getValue() }}>
            <div className="p-4 flex items-start gap-7">
              <div className="space-x-2 flex items-start">
                <button
                  className="border border-[#B7B7B7] rounded p-2"
                  type="button"
                  onClick={() => handleStartCampaign(info.getValue())}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="13" height="16" viewBox="0 0 13 16" fill="none">
                    <path
                      d="M13 7.99999C13.0005 8.20063 12.949 8.39797 12.8507 8.57284C12.7523 8.7477 12.6104 8.89413 12.4386 8.99789L1.79636 15.5082C1.61694 15.6181 1.41144 15.6781 1.20108 15.682C0.990724 15.6859 0.783142 15.6335 0.599773 15.5304C0.418149 15.4288 0.266852 15.2807 0.161439 15.1013C0.0560266 14.9219 0.000303474 14.7177 0 14.5096V1.49039C0.000303474 1.28231 0.0560266 1.07806 0.161439 0.898652C0.266852 0.719243 0.418149 0.571149 0.599773 0.469599C0.783142 0.366448 0.990724 0.314117 1.20108 0.318013C1.41144 0.321908 1.61694 0.381888 1.79636 0.491758L12.4386 7.0021C12.6104 7.10586 12.7523 7.25229 12.8507 7.42715C12.949 7.60202 13.0005 7.79936 13 7.99999Z"
                      fill="#A79877"
                    />
                  </svg>
                </button>
                <div className="flex flex-col">
                  <button
                    className="border border-[#B7B7B7] rounded p-2"
                    type="button"
                    onClick={() => handleOpenDownloadCampaign(info.getValue())}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="16" viewBox="0 0 14 16" fill="none">
                      <path
                        d="M7 12L2.625 7L3.85 5.55L6.125 8.15V0H7.875V8.15L10.15 5.55L11.375 7L7 12ZM1.75 16C1.26875 16 0.856626 15.804 0.513626 15.412C0.170626 15.02 -0.000581848 14.5493 1.48557e-06 14V11H1.75V14H12.25V11H14V14C14 14.55 13.8285 15.021 13.4855 15.413C13.1425 15.805 12.7307 16.0007 12.25 16H1.75Z"
                        fill="#2F2F2F"
                      />
                    </svg>
                  </button>
                  <span className="text-xs">MLS</span>
                </div>
                { info.row.original.type === TYPE_CUSTOM_AUDIENCE ?
                  <div className="flex flex-col items-center">
                    <button
                      className="border border-[#B7B7B7] rounded p-2"
                      type="button"
                      onClick={() => handleOpenDownloadProperty(info.getValue())}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="16" viewBox="0 0 14 16" fill="none">
                        <path
                          d="M7 12L2.625 7L3.85 5.55L6.125 8.15V0H7.875V8.15L10.15 5.55L11.375 7L7 12ZM1.75 16C1.26875 16 0.856626 15.804 0.513626 15.412C0.170626 15.02 -0.000581848 14.5493 1.48557e-06 14V11H1.75V14H12.25V11H14V14C14 14.55 13.8285 15.021 13.4855 15.413C13.1425 15.805 12.7307 16.0007 12.25 16H1.75Z"
                          fill="#2F2F2F"
                        />
                      </svg>
                    </button>
                    <span className="text-xs">Property</span>
                  </div> : <></>
                }
                <button
                  className="border border-[#B7B7B7] rounded p-2"
                  type="button"
                  onClick={() => navigate(`/campaign/${info.getValue()}/edit`)}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="16" viewBox="0 0 14 16" fill="none">
                    <path
                      d="M13.1644 4.08184C13.4951 3.77325 13.6771 3.36344 13.6771 2.9275C13.6771 2.49156 13.4951 2.08174 13.1644 1.77315L11.7766 0.478392C11.4459 0.169805 11.0066 0 10.5394 0C10.0721 0 9.63288 0.169805 9.303 0.477575L0 9.13026V12.7345H3.86138L13.1644 4.08184ZM10.5394 1.63274L11.928 2.92668L10.5367 4.21981L9.149 2.92586L10.5394 1.63274ZM1.75 11.1018V9.80785L7.91 4.07858L9.29775 5.37334L3.13862 11.1018H1.75ZM0 14.3673H14V16H0V14.3673Z"
                      fill="#2F2F2F"
                    />
                  </svg>
                </button>
              </div>
              <button
                className="bg-danger rounded p-2"
                type="button"
                onClick={() => handleOpenDeleteModal(info.getValue())}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="16" viewBox="0 0 14 16" fill="none">
                  <path
                    d="M3.85 0H10.15V2.18182H14V3.63636H12.5797L12.2297 16H1.7703L1.4203 3.63636H0V2.18182H3.85V0ZM5.25 2.18182H8.75V1.45455H5.25V2.18182ZM2.821 3.63636L3.1297 14.5455H10.8703L11.179 3.63636H2.821ZM5.0204 6.00509L7 8.06255L8.9796 6.00509L9.9701 7.03418L7.9898 9.09091L9.9701 11.1476L8.9796 12.1767L7 10.1193L5.0204 12.1767L4.0299 11.1476L6.0102 9.09091L4.0299 7.03418L5.0204 6.00509Z"
                    fill="white"
                  />
                </svg>
              </button>
            </div>
          </Cell>
        ),
        header: () => <span>Actions</span>,
      }),
    ],
    [data?.content, handleOpenDeleteModal, handleOpenDownloadCampaign, handleOpenDownloadProperty, handleStartCampaign, navigate],
  );

  return (
    <div className={cn('border border-gray', 'w-[1280px] mx-auto mt-16', 'rounded-xl')}>
      <div className={cn('px-8 py-6 flex items-center justify-between', 'border-b border-gray')}>
        <div className={cn('flex gap-4 flex-shrink-0 items-center')}>
          <Input
            placeholder="Search for Campaign"
            inputClassname="w-[300px] h-full"
            className="h-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button className="py-2" onClick={handleSearch}>
            Search
          </Button>
          <Dropdown
            label="Sort By"
            value={sort}
            onChange={(val) => setSort(val as string)}
            options={SORT_OPTIONS}
            className="w-[118px] h-9"
            buttonClassname="h-9 bg-blue text-white"
            labelClassname="hidden"
          />
        </div>
        <div className="flex gap-2 items-center">
          <p className="text-sm">Items per page</p>
          <Dropdown
            label="Items Per Page"
            value={itemsPerPage}
            onChange={(val) => setItemsPerPage(val as number)}
            options={ITEMS_PER_PAGE_OPTIONS}
            className="w-[76px] h-9"
            labelClassname="hidden"
            buttonClassname="h-9"
          />
        </div>
      </div>
      <div className={cn('p-8', 'rounded-lg', 'flex justify-center')}>
        <Table serverSideSorting columns={columns as AccessorFnColumnDef<object>[]} data={data?.content ?? []} />
      </div>
      <div className={cn('px-8 py-6', 'flex items-center justify-between', 'mx-auto', 'border-t border-gray')}>
        <div className="flex gap-4 items-center">
          <p className="text-sm">Showing {data?.numberOfElements} entry</p>
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="14" viewBox="0 0 18 14" fill="none">
            <path
              d="M16 6.99997L16.707 6.29297L17.414 6.99997L16.707 7.70697L16 6.99997ZM1 7.99997C0.734784 7.99997 0.48043 7.89461 0.292893 7.70708C0.105357 7.51954 0 7.26518 0 6.99997C0 6.73475 0.105357 6.4804 0.292893 6.29286C0.48043 6.10533 0.734784 5.99997 1 5.99997V7.99997ZM10.707 0.292969L16.707 6.29297L15.293 7.70697L9.293 1.70697L10.707 0.292969ZM16.707 7.70697L10.707 13.707L9.293 12.293L15.293 6.29297L16.707 7.70697ZM16 7.99997H1V5.99997H16V7.99997Z"
              fill="#6F6F6F"
            />
          </svg>
        </div>
        <div className="flex items-center gap-2">
          <button
            disabled={page === 1}
            className="text-sm disabled:text-content"
            onClick={() => setPage((prev) => prev - 1)}
          >
            Prev
          </button>
          {Array.from(Array(data?.totalPages ?? 0).keys()).map((pg) => (
            <button
              onClick={() => setPage(pg + 1)}
              key={pg}
              className={cn(
                'text-sm rounded w-8 h-8',
                'border',
                pg + 1 === page ? 'border-transparent bg-light-blue' : 'border-dark-gray text-dark-gray',
              )}
            >
              {pg + 1}
            </button>
          ))}
          <button
            disabled={page === data?.totalPages ?? 1}
            className="text-sm disabled:text-content"
            onClick={() => setPage((prev) => prev + 1)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default Campaign;
